import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, Link } from "react-router-dom"
import { Row, Col, CardHeader, CardBody, Spinner } from "reactstrap"
import { fetchPlans, fetchCurrentSubscription, scheduleDowngradeAction } from "../../store/subscription/actions"
import DowngradeInfoModal from "../modals/subscription/DowngradeInfoModal"
import { getMyCollectionUsage, getPlanFeatureCatalog, getPlanIntegrationCatalog, getPlanTrialStatus, startPlanTrial } from "../../api/requests/subscription-requests"
import { getReferralOffer } from "../../api/requests/referral-requests"
import { toast, ToastContainer } from "react-toastify"
import NewTicketModal from "../Support/NewTicketModal"
import { formatDaysLeft, formatLicenseDate } from "../../utils/subscriptionDates"
import { resolvePlanPricing } from "../../utils/planPricing"

const TableCard = ({ className = "", children, ...rest }) => (
  <div
    className={`card rounded-4 border-0 shadow-lg ${className}`}
    style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
    {...rest}
  >
    {children}
  </div>
)

const NAVY = "#221C5D"

/**
 * Firmaya yalnızca kendi paketi sunulduğunda gösterilen ekran.
 *
 * Super admin, paketi firmaya atarken "diğer paketler görünsün" seçeneğini
 * işaretlemediyse firma başka paket satın alamaz; bu yüzden paket listesi yerine
 * kendisine atanmış paketin detayları gösterilir.
 */
const AssignedPlanDetail = ({ plan, status, loading, formatPrice, featureLabels = FEATURE_LABELS }) => {
  const [usage, setUsage] = useState(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await getMyCollectionUsage()
        if (!cancelled) setUsage(res?.data || null)
      } catch {
        // Limit bilgisi alınamazsa bölüm gizlenir.
      }
    })()
    return () => { cancelled = true }
  }, [])

  if (loading && !plan && !status) {
    return (
      <div className="page-content">
        <div className="container-fluid text-center p-5">
          <Spinner style={{ color: NAVY }} />
        </div>
      </div>
    )
  }

  const planName = plan?.name || status?.planName || "Paketiniz"
  // Liste yalnız pakete SEÇİLEN özelliklerden üretilir. Eski serbest metin
  // `features` alanı artık gösterilmiyor: aynı maddeleri farklı sözcüklerle
  // tekrarlıyordu ("Excel rapor" ≙ "Excel'e aktarma") ve hiçbir şeyi açıp
  // kapatmadığı için kart, gerçekte açık olan özelliklerle uyuşmuyordu.
  const planFeatures = plan?.planFeatures || status?.planFeatures || []

  const limitRows = [
    { label: "Cari / Bayi", used: status?.currentAccounts, max: status?.maxAccounts },
    { label: "Kullanıcı", used: status?.currentUsers, max: status?.maxUsers },
  ]

  const pct = (used, max) => {
    if (!max || max <= 0) return 0
    return Math.min(100, Math.round((Number(used || 0) / Number(max)) * 100))
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <TableCard>
          <CardHeader className="bg-white border-0 rounded-top-4">
            <h5 className="mb-1 fw-bold text-dark">Aboneliğiniz</h5>
            <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
              Firmanıza özel tanımlanmış paketin detayları
            </p>
          </CardHeader>

          <CardBody className="pt-2">
            {/* Paket başlığı */}
            <div
              className="rounded-4 p-4 mb-4"
              style={{ background: "linear-gradient(135deg,#EBF0F7 0%,#F7F9FC 100%)", border: "1px solid #dfe6f0" }}
            >
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h4 className="mb-0 fw-bold" style={{ color: NAVY }}>{planName}</h4>
                    <span
                      className="badge border-0"
                      style={{ borderRadius: 999, background: "#71C30B", color: "#fff" }}
                    >
                      Aktif
                    </span>
                  </div>
                  {plan?.description && (
                    <div className="text-muted" style={{ fontSize: ".85rem" }}>{plan.description}</div>
                  )}
                </div>
                <div className="text-md-end">
                  {plan?.basePrice != null && (
                    <>
                      {resolvePlanPricing(plan).struck != null && (
                        <div className="text-muted" style={{ fontSize: ".85rem", textDecoration: "line-through" }}>
                          {formatPrice(resolvePlanPricing(plan).struck)}
                        </div>
                      )}
                      <div className="fw-bold" style={{ fontSize: "1.35rem", color: NAVY }}>
                        {formatPrice(resolvePlanPricing(plan).payable)}
                      </div>
                    </>
                  )}
                  {plan?.durationLabel && (
                    <div className="text-muted" style={{ fontSize: ".8rem" }}>{plan.durationLabel}</div>
                  )}
                </div>
              </div>
            </div>

            <Row>
              {/* Dönem bilgisi */}
              <Col md={6} className="mb-3">
                <div className="border rounded-4 p-3 h-100">
                  <h6 className="fw-semibold text-dark mb-3">
                    <i className="bx bx-calendar me-1" style={{ color: NAVY }} />
                    Dönem
                  </h6>
                  <div className="d-flex justify-content-between py-1" style={{ fontSize: ".85rem" }}>
                    <span className="text-muted">Başlangıç</span>
                    <span className="fw-semibold">{status?.startDate || "—"}</span>
                  </div>
                  <div className="d-flex justify-content-between py-1" style={{ fontSize: ".85rem" }}>
                    <span className="text-muted">Bitiş</span>
                    <span className="fw-semibold">{formatLicenseDate(status?.endDate, "—")}</span>
                  </div>
                  <div className="d-flex justify-content-between py-1" style={{ fontSize: ".85rem" }}>
                    <span className="text-muted">Kalan Gün</span>
                    <span className="fw-semibold">{formatDaysLeft(status?.daysRemaining, status?.endDate, "—")}</span>
                  </div>
                </div>
              </Col>

              {/* Kullanım limitleri */}
              <Col md={6} className="mb-3">
                <div className="border rounded-4 p-3 h-100">
                  <h6 className="fw-semibold text-dark mb-3">
                    <i className="bx bx-bar-chart-alt-2 me-1" style={{ color: NAVY }} />
                    Kullanım
                  </h6>
                  {limitRows.map((r) => (
                    <div key={r.label} className="mb-2">
                      <div className="d-flex justify-content-between" style={{ fontSize: ".82rem" }}>
                        <span className="text-muted">{r.label}</span>
                        <span className="fw-semibold">
                          {r.used ?? 0} / {r.max ?? "Sınırsız"}
                        </span>
                      </div>
                      {r.max ? (
                        <div className="progress mt-1" style={{ height: 5 }}>
                          <div
                            className="progress-bar"
                            style={{ width: `${pct(r.used, r.max)}%`, background: NAVY }}
                          />
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </Col>

              {/* Tahsilat limiti */}
              {usage?.limited && (
                <Col md={6} className="mb-3">
                  <div className="border rounded-4 p-3 h-100">
                    <h6 className="fw-semibold text-dark mb-3">
                      <i className="bx bx-wallet me-1" style={{ color: NAVY }} />
                      Tahsilat Limiti
                      <span className="text-muted fw-normal ms-1" style={{ fontSize: ".72rem" }}>
                        ({usage.period === "MONTHLY" ? "bu ay" : "bu dönem"})
                      </span>
                    </h6>

                    {usage.maxAmount != null && (
                      <div className="mb-2">
                        <div className="d-flex justify-content-between" style={{ fontSize: ".82rem" }}>
                          <span className="text-muted">Tutar</span>
                          <span className="fw-semibold">
                            {Number(usage.usedAmount || 0).toLocaleString("tr-TR")} /{" "}
                            {Number(usage.maxAmount).toLocaleString("tr-TR")} ₺
                          </span>
                        </div>
                        <div className="progress mt-1" style={{ height: 5 }}>
                          <div
                            className="progress-bar"
                            style={{
                              width: `${pct(usage.usedAmount, usage.maxAmount)}%`,
                              background: usage.exhausted ? "#E07A7A" : NAVY,
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {usage.maxCount != null && (
                      <div className="mb-2">
                        <div className="d-flex justify-content-between" style={{ fontSize: ".82rem" }}>
                          <span className="text-muted">İşlem adedi</span>
                          <span className="fw-semibold">{usage.usedCount ?? 0} / {usage.maxCount}</span>
                        </div>
                        <div className="progress mt-1" style={{ height: 5 }}>
                          <div
                            className="progress-bar"
                            style={{
                              width: `${pct(usage.usedCount, usage.maxCount)}%`,
                              background: usage.exhausted ? "#E07A7A" : NAVY,
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {usage.exhausted && (
                      <div className="text-danger" style={{ fontSize: ".75rem" }}>
                        <i className="bx bx-error-circle me-1" />
                        Limit doldu; yeni tahsilat yapılamıyor.
                      </div>
                    )}
                  </div>
                </Col>
              )}

              {/* Paket özellikleri */}
              <Col md={usage?.limited ? 6 : 12} className="mb-3">
                <div className="border rounded-4 p-3 h-100">
                  <h6 className="fw-semibold text-dark mb-3">
                    <i className="bx bx-check-shield me-1" style={{ color: NAVY }} />
                    Paket Kapsamı
                  </h6>
                  {planFeatures.length === 0 ? (
                    <div className="text-muted" style={{ fontSize: ".82rem" }}>
                      Bu paket için tanımlı özellik bulunmuyor.
                    </div>
                  ) : (
                    <Row>
                      {planFeatures.map((f) => featureLabels[f] || f).map((label, i) => (
                        <Col md={6} key={`${label}-${i}`}>
                          <div className="d-flex align-items-start gap-2 py-1" style={{ fontSize: ".82rem" }}>
                            <i className="bx bx-check" style={{ color: "#71C30B", marginTop: 2 }} />
                            <span>{label}</span>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  )}
                </div>
              </Col>
            </Row>

            <div className="text-muted text-center mt-2" style={{ fontSize: ".78rem" }}>
              Paketiniz firmanıza özel tanımlanmıştır. Değişiklik için lütfen yetkilinizle iletişime geçin.
            </div>
          </CardBody>
        </TableCard>
      </div>
    </div>
  )
}

/**
 * Özellik kodlarının Türkçe karşılıkları — <b>yedek</b>.
 * Asıl kaynak backend kataloğudur ({@code /api/subscriptions/plan-features}); bu liste
 * yalnızca istek başarısız olursa devreye girer.
 */
/**
 * Özellik etiketlerinin YEDEK listesi — gerçek kaynak backend'deki PlanFeature
 * kataloğu (getPlanFeatureCatalog). Bu harita yalnız katalog isteği dönene kadar
 * geçen anda kullanılır. Eksiktir ve zamanla geride kalır (ör. ACCOUNTING_INTEGRATION
 * V98'de kaldırıldı, burada duruyor); etiket değiştirirken asıl yer enum'dur.
 */
const FEATURE_LABELS = {
  CREDIT_CARD_COLLECTION: "Kredi kartı ile tahsilat",
  INSTALLMENT: "Taksitli tahsilat",
  REFUND: "Tam iade",
  PAYMENT_LINK: "Ödeme linki oluşturma",
  FIXED_PAYMENT_LINK: "Sabit (çok kullanımlı) ödeme linki",
  PAYMENT_LINK_EMAIL: "E-posta ile ödeme linki gönderimi",
  PAYMENT_LINK_SMS: "SMS ile ödeme linki gönderimi",
  DEBT_REMINDER: "Vade takip ve ödeme hatırlatma",
  RECONCILIATION: "Cari mutabakat",
  SCHEDULED_REPORTS: "Otomatik rapor gönderimi",
  POS_ANALYTICS: "POS rapor ve analiz",
  CUSTOM_DOMAIN: "Firmaya ait özel alan adı (domain)",
  PRIORITY_SUPPORT: "Öncelikli destek",
}

// Renkler kurumsal kimlikten (docs/tahsilat-360-kurumsal-kimlik.pdf): lacivert,
// turuncu ve tonları, gri. Ücretsiz paket gri, ücretliler turuncu → lacivert.
const FREE_COLOR = { accent: "#5B5C63", light: "#F1F1F3" }
const PLAN_COLORS = [
  { accent: "#E56A22", light: "#FDEEE4" },
  { accent: "#221C5D", light: "#E9E8F1" },
  { accent: "#EA7C3B", light: "#FEF3EC" },
]
const QUOTE_COLOR = { accent: "#E56A22", light: "rgba(255,255,255,.12)" }

const PlanSelection = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { plans, loading, currentStatus, downgradeLoading } = useSelector((state) => state.subscription)

  const [quotePlan, setQuotePlan] = useState(null)
  const headerRefs = useRef({})

  const [downgradeModal, setDowngradeModal] = useState(false)
  const [selectedDowngradePlan, setSelectedDowngradePlan] = useState(null)
  // Davetle gelen firma: seçeceği ilk pakette hediye ay bekliyor.
  const [referralOffer, setReferralOffer] = useState(null)

  useEffect(() => {
    getReferralOffer()
      .then((res) => setReferralOffer(res?.data?.eligible ? res.data : null))
      .catch(() => setReferralOffer(null))
  }, [])

  const styles = `
    .plans-select-modern .plan-card {
      border: 2px solid #e9ecef;
      border-radius: 1rem;
      transition: all .25s ease;
      overflow: hidden;
    }
    .plans-select-modern .plan-card:hover {
      transform: translateY(-6px);
      box-shadow: 0 1rem 2rem rgba(0,0,0,.12);
    }
    .plans-select-modern .plan-header {
      padding: 1.25rem 1.1rem .9rem;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    .plans-select-modern .plan-header > * { max-width: 100%; }
    .plans-select-modern .plan-card.is-quote {
      background: #221C5D !important;
      border-color: rgba(229,106,34,.6) !important;
      box-shadow: 0 .75rem 2rem rgba(34,28,93,.3);
      color: rgba(255,255,255,.9);
    }
    .plans-select-modern .plan-card.is-quote:hover { box-shadow: 0 1.25rem 2.5rem rgba(34,28,93,.4); }
    .plans-select-modern .is-quote .plan-header {
      background: radial-gradient(120% 90% at 50% 0%, rgba(229,106,34,.25) 0%, rgba(229,106,34,0) 70%) !important;
    }
    .plans-select-modern .is-quote .plan-name { color: #fff !important; letter-spacing: .02em; }
    .plans-select-modern .is-quote .plan-desc { color: rgba(255,255,255,.72) !important; }
    .plans-select-modern .is-quote .plan-quote { color: #F29A64 !important; }
    .plans-select-modern .is-quote .feature-icon { color: #F29A64 !important; }
    .plans-select-modern .is-quote .feature-title,
    .plans-select-modern .is-quote .integration-cat { color: #F29A64; }
    .plans-select-modern .is-quote .integration-chip { color: #fff !important; background: rgba(255,255,255,.12) !important; }
    .plans-select-modern .is-quote .border-top { border-color: rgba(255,255,255,.15) !important; }
    .plans-select-modern .is-quote .limit-row { color: rgba(255,255,255,.7); }
    .plans-select-modern .is-quote .limit-row .fw-medium { color: #fff !important; }
    .plans-select-modern .is-quote .buy-btn { color: #fff; box-shadow: 0 .4rem 1rem rgba(229,106,34,.35); }
    .plans-select-modern .is-quote .feature-fill::-webkit-scrollbar-thumb { background: rgba(255,255,255,.25); }
    .plans-select-modern .is-quote .feature-fill { scrollbar-color: rgba(255,255,255,.25) transparent; }
    .plans-select-modern .plan-name-hero {
      font-size: 2.1rem;
      font-weight: 800;
      line-height: 1.15;
      letter-spacing: -.01em;
    }
    .plans-select-modern .plan-quote {
      font-size: 1.6rem;
      font-weight: 800;
      line-height: 1.2;
    }
    .plans-select-modern .plan-body {
      padding: 0 1.1rem 1.25rem;
    }
    .plans-select-modern .plan-name {
      font-size: 1.15rem;
    }
    .plans-select-modern .plan-desc {
      font-size: .82rem;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .plans-select-modern .plan-price {
      font-size: 1.75rem;
      font-weight: 800;
      line-height: 1.2;
    }
    .plans-select-modern .feature-title {
      font-size: .72rem; text-transform: uppercase; letter-spacing: .04em;
      color: #9aa2b1; font-weight: 700; margin-bottom: .35rem;
    }
    .plans-select-modern .plan-desc { min-height: 2.46rem; }
    /* Kartlar yan yanayken en az özellikli paket listesini tam gösterir ve kart
       boyunu o belirler; diğerleri kalan alanı doldurup gerisini kaydırır. */
    @media (min-width: 1200px) {
      .plans-select-modern .feature-fill {
        flex: 1 1 0;
        min-height: 240px;
        contain: size;
        overflow-y: auto;
        padding-right: .35rem;
        padding-bottom: 1.75rem;
        scrollbar-width: thin;
        -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - 2.5rem), transparent);
        mask-image: linear-gradient(to bottom, #000 calc(100% - 2.5rem), transparent);
      }
    }
    .plans-select-modern .feature-fill::-webkit-scrollbar { width: 5px; }
    .plans-select-modern .feature-fill::-webkit-scrollbar-thumb { background: #d5d9e0; border-radius: 999px; }
    .plans-select-modern .plan-price-old {
      font-size: .9rem;
      color: #98a0ad;
      text-decoration: line-through;
      text-decoration-thickness: 1.5px;
    }
    .plans-select-modern .plan-discount-badge {
      display: inline-flex;
      align-items: center;
      gap: .2rem;
      vertical-align: middle;
      font-size: .72rem;
      font-weight: 800;
      letter-spacing: .02em;
      padding: .18rem .55rem .18rem .45rem;
      border-radius: 999px;
      color: #fff;
      white-space: nowrap;
      box-shadow: 0 2px 6px rgba(0,0,0,.12);
    }
    .plans-select-modern .plan-discount-badge i { font-size: .85rem; }
    .plans-select-modern .feature-item {
      display: flex;
      align-items: flex-start;
      gap: .45rem;
      margin-bottom: .45rem;
      font-size: .85rem;
    }
    .plans-select-modern .integration-cat {
      font-size: .62rem; text-transform: uppercase; letter-spacing: .04em;
      color: #9aa2b1; font-weight: 700; margin-bottom: .2rem;
    }
    .plans-select-modern .integration-chip {
      display: inline-block; border-radius: 999px; padding: .12rem .5rem;
      font-size: .68rem; font-weight: 600; white-space: nowrap;
    }
    .plans-select-modern .feature-icon {
      flex-shrink: 0;
      margin-top: 1px;
      font-size: 1.05rem;
    }
    .plans-select-modern .buy-btn {
      border-radius: 999px;
      font-weight: 600;
      font-size: .95rem;
      padding: .55rem 1.25rem;
      border: none;
      color: #fff;
      transition: filter .2s;
    }
    .plans-select-modern .buy-btn:hover { filter: brightness(1.08); }
    .plans-select-modern .buy-btn:disabled { opacity: .55; cursor: default; filter: none; }
    .plans-select-modern .current-badge {
      position: absolute; top: -1px; right: -1px;
      border-radius: 0 1rem 0 1rem;
      padding: .35rem .9rem;
      font-size: .7rem; font-weight: 700;
      color: #fff; letter-spacing: .3px;
    }
    .plans-select-modern .plan-card.is-current {
      border-width: 3px;
      box-shadow: 0 .5rem 1.5rem rgba(0,0,0,.1);
    }
    .plans-select-modern .remaining-info {
      display: flex; align-items: center; justify-content: center; gap: .35rem;
      font-size: .8rem; color: #6c757d; margin-top: .5rem;
    }
    .plans-select-modern .limit-row {
      display: flex;
      justify-content: space-between;
      font-size: .8rem;
      color: #6c757d;
      padding: .2rem 0;
    }
  `

  document.title = "Abonelik Paketleri | Tahsilat360"

  // Özellik kataloğu: kod → etiket. Backend'den gelir ki PlanFeature enum'una
  // eklenen bir özellik burada da doğru adla görünsün.
  const [featureLabels, setFeatureLabels] = useState(FEATURE_LABELS)
  // Entegrasyon kataloğu: kod → etiket/kategori. Uçtan gelir ki yeni bir sağlayıcı
  // eklendiğinde paket kartları kendiliğinden güncellensin.
  const [integrationCatalog, setIntegrationCatalog] = useState([])
  // Paket denemesi: pencere firmaya ait, kaç paket denenirse denensin uzamaz.
  const [planTrial, setPlanTrial] = useState(null)
  const [trialStarting, setTrialStarting] = useState(null)

  useEffect(() => {
    dispatch(fetchPlans())
    dispatch(fetchCurrentSubscription())
  }, [dispatch])

  const loadPlanTrial = useCallback(async () => {
    try {
      const res = await getPlanTrialStatus()
      setPlanTrial(res?.data || null)
    } catch {
      // Durum alınamazsa dene düğmesi çizilmez; sayfanın kalanı çalışır.
    }
  }, [])

  useEffect(() => { loadPlanTrial() }, [loadPlanTrial])

  const handleStartTrial = async (plan) => {
    setTrialStarting(plan.id)
    try {
      const res = await startPlanTrial(plan.id)
      toast.success(res.message || "Deneme başlatıldı", { position: "top-right", autoClose: 3000 })
      setPlanTrial(res?.data || null)
      dispatch(fetchCurrentSubscription())
    } catch (err) {
      toast.error(err.message, { position: "top-right", autoClose: 4000 })
    } finally {
      setTrialStarting(null)
    }
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await getPlanFeatureCatalog()
        if (cancelled || !res?.data) return
        const map = {}
        res.data.forEach((f) => { map[f.code] = f.label })
        setFeatureLabels((prev) => ({ ...prev, ...map }))
      } catch {
        // Katalog alınamazsa yedek etiketler kullanılır.
      }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await getPlanIntegrationCatalog()
        if (!cancelled) setIntegrationCatalog(res?.data || [])
      } catch {
        // Katalog alınamazsa kartlarda entegrasyon bölümü çizilmez; paket
        // listesinin geri kalanı çalışmaya devam eder.
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Mevcut planı bul (plans array'den currentPlan === true olan)
  const currentPlanData = plans.find((p) => p.currentPlan === true)

  const handleDowngradeClick = (plan) => {
    setSelectedDowngradePlan(plan)
    setDowngradeModal(true)
  }

  const handleDowngradeConfirm = (targetPlanId, reason) => {
    if ((selectedDowngradePlan?.totalPrice || 0) > 0) {
      navigate(`/subscription/checkout/${targetPlanId}`)
      return
    }
    dispatch(scheduleDowngradeAction(targetPlanId, reason))
    setDowngradeModal(false)
    setSelectedDowngradePlan(null)
  }

  const formatPrice = (price) =>
    new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price) + " ₺"

  const resolvePricing = resolvePlanPricing

  /**
   * Kart genişliği paket sayısına göre belirlenir:
   *   1–4 paket → hepsi TEK SATIRDA (12/n kolon)
   *   5 ve üzeri → satır başına 3 kart, yani 5 paket "üstte 3, altta 2"
   * Row `justify-content-center` taşıdığı için eksik kalan son satır ortalanır.
   */
  const cardXl = plans.length > 0 && plans.length <= 4 ? Math.floor(12 / plans.length) : 4

  // Renkli başlıklar aynı yükseklikte kalsın: en uzun başlığın boyu hepsine uygulanır.
  useLayoutEffect(() => {
    const equalize = () => {
      const els = Object.values(headerRefs.current).filter(Boolean)
      els.forEach((el) => { el.style.minHeight = "" })
      const max = Math.max(0, ...els.map((el) => el.offsetHeight))
      els.forEach((el) => { el.style.minHeight = `${max}px` })
    }
    equalize()
    window.addEventListener("resize", equalize)
    return () => window.removeEventListener("resize", equalize)
  }, [plans, planTrial])

  const isFreePlan = (plan) => !plan.quoteOnly && Number(resolvePricing(plan).payable || 0) === 0

  // Ücretli kartlarda fiyatlar aynı hizada dursun: bir kartta olan satır diğerlerinde boş yer tutar.
  const anyStruck = plans.some((p) => !p.quoteOnly && resolvePricing(p).struck != null)
  const anyDescription = plans.some((p) => p.description)
  const anyRemaining = plans.some((p) => p.currentPlan === true && p.remainingDays != null)
  const anchorPlanId = plans.reduce(
    (min, p) => (!min || (p.planFeatures || []).length < (min.planFeatures || []).length ? p : min),
    null
  )?.id

  // Firmaya başka paket sunulmuyorsa (super admin "diğer paketler görünsün" demediyse)
  // paket listesi yerine, atanmış paketin detay ekranı gösterilir. Backend zaten
  // yalnız firmanın kendi paketini döndürür; sunum da ona göre değişir.
  if (currentStatus?.offerOtherPlans === false) {
    return (
      <AssignedPlanDetail
        plan={currentPlanData || plans[0]}
        status={currentStatus}
        loading={loading}
        formatPrice={formatPrice}
        featureLabels={featureLabels}
      />
    )
  }

  return (
    <div className="page-content plans-select-modern">
      <style>{styles}</style>
      <div className="container-fluid">
        <Row>
          <Col>
            <TableCard>
              <CardHeader data-tour="subscription-plans" className="bg-white border-0 rounded-top-4">
                <div className="d-none d-md-flex justify-content-between align-items-start">
                  <div>
                    <h5 className="mb-1 fw-bold text-dark">Abonelik Paketleri</h5>
                    <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                      Firmanız için en uygun paketi seçin
                    </p>
                  </div>
                </div>
                <div className="d-md-none">
                  <h5 className="mb-1 fw-semibold">Paketler</h5>
                  <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>En uygun paketi seçin</p>
                </div>
              </CardHeader>

              <CardBody className="pt-2">
                {referralOffer && (
                  <div className="alert alert-info d-flex align-items-center gap-2 py-2" style={{ borderRadius: 12 }}>
                    <i className="bx bx-gift" style={{ fontSize: 22 }} />
                    <div>
                      Davet ile geldiniz: seçtiğiniz pakete{" "}
                      <strong>{referralOffer.months} ay hediye</strong> eklenir.
                    </div>
                  </div>
                )}
                <div className="text-center mb-4">
                  <Link
                    to="/subscription/compare"
                    className="text-decoration-none d-inline-flex align-items-center gap-2"
                    style={{
                      color: NAVY,
                      background: "#EBF0F7",
                      padding: ".55rem 1.25rem",
                      borderRadius: 999,
                      fontWeight: 600,
                      fontSize: ".9rem",
                      transition: "all .2s",
                    }}
                  >
                    <i className="bx bx-columns" />
                    Planları Karşılaştır
                    <i className="bx bx-chevron-right" style={{ fontSize: ".85rem" }} />
                  </Link>
                </div>

                {loading && plans.length === 0 ? (
                  <div className="text-center p-5">
                    <Spinner size="lg" color="primary" />
                    <h6 className="mt-3 text-muted">Paketler yükleniyor...</h6>
                  </div>
                ) : plans.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bx bx-package display-4 d-block mb-2 text-muted" />
                    <p className="text-muted">Henüz aktif paket bulunmamaktadır.</p>
                  </div>
                ) : (
                  <Row className="g-3 justify-content-center">
                    {plans.map((plan, index) => {
                      // Yalnız pakete SEÇİLEN özellikler. Eski serbest metin `features`
                      // alanı bilerek okunmuyor — bkz. AssignedPlanDetail'deki not.
                      const featureList = (plan.planFeatures || []).map((c) => featureLabels[c] || c)
                      // Paketin izin verdiği entegrasyonlar. Boş küme "kısıtlama yok"
                      // demektir; o pakette katalogun tamamı geçerlidir.
                      const planIntegrationCodes = (plan.planIntegrations && plan.planIntegrations.length > 0)
                        ? plan.planIntegrations
                        : integrationCatalog.map((i) => i.code)
                      const integrationRows = integrationCatalog
                        .filter((i) => planIntegrationCodes.includes(i.code))
                        .reduce((acc, i) => {
                          const row = acc.find((x) => x.category === i.category)
                          if (row) row.items.push(i.label)
                          else acc.push({ category: i.category, items: [i.label] })
                          return acc
                        }, [])

                      const color = plan.quoteOnly
                        ? QUOTE_COLOR
                        : isFreePlan(plan)
                          ? FREE_COLOR
                          : PLAN_COLORS[plans.filter((p) => !p.quoteOnly && !isFreePlan(p)).indexOf(plan) % PLAN_COLORS.length]
                      const isCurrent = plan.currentPlan === true
                      const purchaseType = plan.purchaseType // NEW, RENEWAL, UPGRADE, DOWNGRADE, CURRENT

                      const getBtnLabel = () => {
                        if (isCurrent && purchaseType !== "RENEWAL") return { icon: "bx-check-circle", text: "Mevcut Paketiniz" }
                        switch (purchaseType) {
                          case "RENEWAL": {
                            const endDateStr = plan.currentPlanEndDate
                              ? new Date(plan.currentPlanEndDate).toLocaleDateString("tr-TR")
                              : null
                            return {
                              icon: "bx-refresh",
                              text: endDateStr
                                ? `Yenile (${endDateStr}'den itibaren)`
                                : "Dönem Sonunda Yenile",
                            }
                          }
                          case "UPGRADE": return { icon: "bx-up-arrow-alt", text: "Yükselt" }
                          case "DOWNGRADE": return { icon: "bx-down-arrow-alt", text: "Düşür" }
                          case "NEXT_PERIOD_TAKEN": {
                            const startStr = plan.nextPeriodStartDate
                              ? new Date(plan.nextPeriodStartDate).toLocaleDateString("tr-TR")
                              : ""
                            return { icon: "bx-calendar-check", text: `${plan.nextPeriodPlanName} ${startStr}'den itibaren ödendi` }
                          }
                          default: return { icon: "bx-cart", text: "Satın Al" }
                        }
                      }
                      const btn = getBtnLabel()
                      const btnDisabled = (isCurrent && purchaseType !== "RENEWAL") || purchaseType === "NEXT_PERIOD_TAKEN"

                      return (
                        <Col
                          key={plan.id}
                          xl={cardXl}
                          lg={plans.length >= 3 ? 4 : 6}
                          md={6}
                          sm={12}
                        >
                          <div
                            className={`plan-card h-100 d-flex flex-column bg-white ${isCurrent ? "is-current" : ""} ${plan.quoteOnly ? "is-quote" : ""}`}
                            style={{ borderColor: isCurrent ? color.accent : color.accent + "40", position: "relative" }}
                          >
                            {/* Mevcut paket badge */}
                            {isCurrent && (
                              <div className="current-badge" style={{ backgroundColor: color.accent }}>
                                <i className="bx bx-check me-1" />Mevcut
                              </div>
                            )}

                            {/* Header */}
                            <div
                              className="plan-header"
                              ref={(el) => { headerRefs.current[plan.id] = el }}
                              style={{ backgroundColor: color.light }}
                            >
                              <span
                                className="badge mb-1"
                                style={{
                                  borderRadius: 999,
                                  backgroundColor: color.accent,
                                  color: "#fff",
                                  fontSize: ".72rem",
                                  padding: ".3rem .7rem",
                                }}
                              >
                                {plan.durationLabel || "—"}
                              </span>
                              <h6
                                className={`${isFreePlan(plan) ? "plan-name-hero" : "plan-name fw-bold"} mb-1`}
                                style={{ color: color.accent }}
                              >
                                {plan.name}
                              </h6>
                              {anyDescription && (
                                <p className="plan-desc text-muted mb-1" title={plan.description || undefined}>{plan.description}</p>
                              )}

                              {/* Price */}
                              {plan.quoteOnly ? (
                                <div className="mb-1">
                                  {anyStruck && <div style={{ visibility: "hidden" }}><span className="plan-price-old">&nbsp;</span></div>}
                                  <div className="plan-quote" style={{ color: color.accent }}>Teklif Alın</div>
                                </div>
                              ) : !isFreePlan(plan) && (() => {
                                const pricing = resolvePricing(plan)
                                return (
                                  <div className="mb-1">
                                    {anyStruck && (
                                      // Satır içi öğeler: kapsayıcı .plan-header'ın
                                      // text-align:center'ını miras alsınlar. Flex
                                      // kapsayıcı kullanılırsa hizalama sola kayıyor.
                                      <div style={pricing.struck == null ? { visibility: "hidden" } : undefined}>
                                        <span className="plan-price-old">
                                          {formatPrice(pricing.struck ?? pricing.payable ?? 0)}
                                        </span>
                                        {pricing.percentage > 0 && (
                                          <span className="plan-discount-badge ms-2" style={{ background: color.accent }}>
                                            <i className="bx bxs-purchase-tag" />
                                            %{pricing.percentage} İndirim
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    <div className="plan-price" style={{ color: color.accent }}>
                                      {formatPrice(pricing.payable)}
                                    </div>
                                  </div>
                                )
                              })()}
                              {/* Kalan gün bilgisi */}
                              {isCurrent && plan.remainingDays != null && (
                                <div className="remaining-info">
                                  <i className="bx bx-time-five" />
                                  <span>
                                    {plan.remainingDays > 0
                                      ? <><strong>{plan.remainingDays}</strong> gün kaldı</>
                                      : "Süreniz doldu"}
                                    {plan.currentPlanEndDate && (
                                      <> · {new Date(plan.currentPlanEndDate).toLocaleDateString("tr-TR")}</>
                                    )}
                                  </span>
                                </div>
                              )}
                              {anyRemaining && !(isCurrent && plan.remainingDays != null) && (
                                <div className="remaining-info" style={{ visibility: "hidden" }}>&nbsp;</div>
                              )}
                            </div>

                            {/* Body */}
                            <div className="plan-body flex-grow-1 d-flex flex-column pt-2">
                              {/* Features */}
                              {featureList.length > 0 && (
                                <div className="mb-2 flex-grow-1 d-flex flex-column">
                                  <div className="feature-title">Özellikler ({featureList.length})</div>
                                  <div className={plan.id === anchorPlanId ? undefined : "feature-fill"}>
                                    {featureList.map((feature, idx) => (
                                      <div key={idx} className="feature-item">
                                        <i
                                          className="bx bx-check-circle feature-icon"
                                          style={{ color: color.accent }}
                                        />
                                        <span>{feature}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Entegrasyonlar — paketin kapsadığı sanal POS ve muhasebe */}
                              {integrationRows.length > 0 && (
                                <div className="border-top pt-2 mb-2">
                                  {integrationRows.map((row) => (
                                    <div key={row.category} className="mb-2">
                                      <div className="integration-cat">{row.category}</div>
                                      <div className="d-flex flex-wrap gap-1">
                                        {row.items.map((label) => (
                                          <span
                                            key={label}
                                            className="integration-chip"
                                            style={{ background: `${color.light}`, color: color.accent }}
                                          >
                                            {label}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Limits */}
                              <div className="border-top pt-2 mb-2">
                                <div className="limit-row">
                                  <span>Maks. Cari</span>
                                  <span className="fw-medium text-dark">
                                    {plan.maxAccounts || "Sınırsız"}
                                  </span>
                                </div>
                                <div className="limit-row">
                                  <span>Maks. Kullanıcı</span>
                                  <span className="fw-medium text-dark">
                                    {plan.maxUsers || "Sınırsız"}
                                  </span>
                                </div>
                                <div className="limit-row">
                                  <span>Taksit Grubu</span>
                                  <span className="fw-medium text-dark">
                                    {plan.maxAccountGroups || "Sınırsız"}
                                  </span>
                                </div>
                                <div className="limit-row">
                                  <span>Özel Domain</span>
                                  <span className="fw-medium" style={{ color: plan.allowCustomDomain ? NAVY : "#B0B7C3" }}>
                                    {plan.allowCustomDomain ? "Var" : "Yok"}
                                  </span>
                                </div>
                              </div>

                              {/* Buy / Current */}
                              {plan.quoteOnly && !isCurrent ? (
                                <button
                                  className="buy-btn w-100"
                                  style={{ backgroundColor: color.accent }}
                                  onClick={() => setQuotePlan(plan)}
                                >
                                  <i className="bx bx-envelope me-1" />
                                  Bize Ulaşın
                                </button>
                              ) : (
                              <button
                                className="buy-btn w-100"
                                style={{
                                  backgroundColor: btnDisabled ? "#B0B7C3" : color.accent,
                                }}
                                disabled={btnDisabled}
                                onClick={() => {
                                  if (btnDisabled) return
                                  if (purchaseType === "DOWNGRADE") {
                                    handleDowngradeClick(plan)
                                  } else if (purchaseType === "UPGRADE") {
                                    navigate(`/subscription/upgrade/${plan.id}`)
                                  } else {
                                    navigate(`/subscription/checkout/${plan.id}`)
                                  }
                                }}
                              >
                                <i className={`bx ${btn.icon} me-1`} />
                                {btn.text}
                              </button>
                              )}

                              {/* Deneme düğmesi yalnız denemeye açık paketlerde ve pencere
                                  kapanmadıysa çıkar. Pencere firmaya ait: başka bir paketi
                                  denemek süreyi uzatmaz, kalan günden devam eder.
                                  Alt seviye pakette çıkmaz: parasını ödediği kapsamı
                                  14 günlüğüne daraltmak isteyen olmaz. */}
                              {plan.trialAllowed && !isCurrent && planTrial
                                && purchaseType !== "DOWNGRADE"
                                && (!planTrial.windowStarted || planTrial.windowOpen)
                                && planTrial.activePlanId !== plan.id && (
                                <button
                                  className="buy-btn w-100 mt-2"
                                  style={{ backgroundColor: "transparent", color: color.accent, border: `1px solid ${color.accent}` }}
                                  disabled={trialStarting === plan.id}
                                  onClick={() => handleStartTrial(plan)}
                                >
                                  {trialStarting === plan.id ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    <>
                                      <i className="bx bx-time-five me-1" />
                                      {planTrial.windowStarted
                                        ? `${planTrial.daysRemaining} Gün Dene`
                                        : `${planTrial.trialDays} Gün Ücretsiz Dene`}
                                    </>
                                  )}
                                </button>
                              )}

                              {plan.trialAllowed && planTrial?.activePlanId === plan.id && (
                                <div className="text-center mt-2" style={{ fontSize: ".78rem", color: color.accent, fontWeight: 600 }}>
                                  <i className="bx bx-check-circle me-1" />
                                  Deneniyor · {planTrial.daysRemaining} gün kaldı
                                </div>
                              )}
                            </div>
                          </div>
                        </Col>
                      )
                    })}
                  </Row>
                )}
              </CardBody>
            </TableCard>
          </Col>
        </Row>
      </div>

      {/* Downgrade Modal */}
      <DowngradeInfoModal
        isOpen={downgradeModal}
        toggle={() => {
          setDowngradeModal(false)
          setSelectedDowngradePlan(null)
        }}
        currentPlan={currentPlanData || null}
        targetPlan={selectedDowngradePlan}
        currentSubscription={currentStatus}
        onConfirm={handleDowngradeConfirm}
        loading={downgradeLoading}
      />

      <NewTicketModal
        isOpen={!!quotePlan}
        toggle={() => setQuotePlan(null)}
        onCreated={(newId) => {
          setQuotePlan(null)
          navigate(newId ? `/support/${newId}` : "/support")
        }}
        initialValues={quotePlan ? {
          subject: `${quotePlan.name} paket teklifi`,
          category: "BILLING",
          message: `${quotePlan.name} paketi için fiyat teklifi almak istiyoruz.`,
        } : undefined}
      />

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  )
}

export default PlanSelection
